'use client'

import { useContext, useState, useMemo, useCallback } from 'react'
import { AuthContext } from '@/contexts/AuthContext'
import { ManagerContext } from '@/contexts/ManagerContext'
import { useRouter, usePathname } from 'next/navigation'
import { objectIdIsEmpty, hasSomeAccess } from '@/lib/utils/common'
import { strs } from '@/lib/strings'
import { flagEnabled } from '@/hooks/useFeatureFlag'
import {
  LayoutDashboard,
  LogOut,
  Building,
  ScrollText,
  Settings,
  Crown,
  Shield,
  Layers,
  Activity,
  ClipboardCheck,
  FileCheck,
  Boxes,
  Radar,
  Webhook,
} from 'lucide-react'
import OrganizationModal from '@/components/common/modals/OrganizationModal'
import UsageWarning from '@/components/common/feedback/UsageWarning'
import TeamSelector from '@/components/common/TeamSelector'
import NotificationBell from '@/app/manager/menu/NotificationBell'
import Link from 'next/link'
import {
  Sidebar as SidebarRoot,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from '@/components/ui/shadcn/sidebar'

const navGroups = [
  {
    label: null,
    scope: 'team',
    items: [
      {
        name: 'Dashboard',
        icon: LayoutDashboard,
        href: '/manager/dashboard',
        id: 'dashboard-button',
      },
      {
        name: 'Command Center',
        icon: Radar,
        href: '/manager/command-center',
        id: 'command-center-button',
        permission: 'viewCriticalDependencies',
      },
      {
        name: 'Drift',
        icon: Activity,
        href: '/manager/drift',
        id: 'drift-button',
        permission: 'viewServiceReadiness',
      },
    ],
  },
  {
    label: 'Plan',
    scope: 'org',
    items: [
      {
        name: 'Services',
        icon: Layers,
        href: '/manager/services',
        id: 'services-button',
        permission: 'viewServices',
      },
      {
        name: 'Critical Dependencies',
        nameKey: 'CriticalDependencies',
        icon: Boxes,
        href: '/manager/critical-dependencies',
        id: 'critical-dependencies-button',
        permission: 'viewCriticalDependencies',
      },
    ],
  },
  {
    label: 'Proof',
    scope: 'org',
    items: [
      {
        name: 'Assumptions',
        nameKey: 'Assumptions',
        icon: ClipboardCheck,
        href: '/manager/assumptions',
        id: 'assumptions-button',
        permission: 'viewAssumptions',
      },
      {
        name: 'Evidence',
        nameKey: 'Evidence',
        icon: FileCheck,
        href: '/manager/evidence',
        id: 'evidence-button',
        permission: 'viewEvidence',
      },
    ],
  },
  {
    label: 'Platform',
    scope: 'team',
    items: [
      {
        name: 'Triggers',
        icon: Webhook,
        href: '/manager/triggers',
        id: 'triggers-button',
        permission: 'viewTriggers',
      },
      {
        name: 'Settings',
        icon: Settings,
        href: '/manager/settings',
        id: 'settings-button',
      },
    ],
  },
]

const isActive = (pathname, href) => new RegExp(`^${href}`).test(pathname)

export default function Sidebar() {
  const authContext = useContext(AuthContext)
  const managerContext = useContext(ManagerContext)
  const router = useRouter()
  const pathname = usePathname()
  const [openChooseOrg, setOpenChooseOrg] = useState(false)
  const [orgModalPage, setOrgModalPage] = useState('choose')

  const logout = () => authContext.signout(() => router.replace('/'))

  const noAccess = useMemo(() => {
    if (!authContext.user?.RBACProfile?.permissions) return true
    return !hasSomeAccess(authContext.user.RBACProfile.permissions)
  }, [authContext.user?.RBACProfile])

  const getNavName = useCallback(
    item =>
      item.nameKey
        ? strs(item.nameKey, authContext.user?.organization)
        : item.name,
    [authContext.user?.organization],
  )

  return (
    <SidebarRoot collapsible="icon">
      <SidebarHeader>
        <div className="flex items-center gap-1">
          <div className="flex-1">
            <TeamSelector />
          </div>
          <NotificationBell />
        </div>
      </SidebarHeader>

      <SidebarContent>
        {navGroups.map((group, gi) => {
          const items = group.items.filter(item => {
            if (
              item.permission &&
              !authContext.user?.isAuthorized?.(item.permission)
            )
              return false
            if (
              item.flag &&
              !flagEnabled(authContext.user?.organization, item.flag)
            )
              return false
            if (item.name !== 'Dashboard' && noAccess) return false
            return true
          })
          if (items.length === 0) return null

          return (
            <SidebarGroup key={gi}>
              {group.label && (
                <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
              )}
              <SidebarGroupContent>
                <SidebarMenu>
                  {items.map(item => {
                    const Icon = item.icon
                    const active = isActive(pathname, item.href)
                    const isPro =
                      item.pro && managerContext.billingDetails?.tier === 'Free'
                    return (
                      <SidebarMenuItem key={item.name}>
                        <SidebarMenuButton
                          asChild
                          isActive={active}
                          tooltip={getNavName(item)}
                        >
                          <Link href={item.href} id={item.id}>
                            <Icon />
                            <span>{getNavName(item)}</span>
                            {isPro && (
                              <Crown className="ml-auto size-3 text-amber-500" />
                            )}
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )
        })}
      </SidebarContent>

      <SidebarFooter>
        <UsageWarning />
        <SidebarMenu>
          {authContext.user?.organization &&
            !objectIdIsEmpty(authContext.user.organization.id) && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={() => setOpenChooseOrg(true)}
                  tooltip="Switch organization"
                >
                  <Building />
                  <span className="truncate">
                    {authContext.user.organization.name}
                  </span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}
          {authContext.user.isAdmin && (
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={isActive(pathname, '/manager/admin')}
                tooltip="Administration"
              >
                <Link href="/manager/admin">
                  <Shield />
                  <span>Administration</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          {authContext.user?.isAuthorized('viewAuditLogs') && (
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={isActive(pathname, '/manager/audit')}
                tooltip="Audit Log"
              >
                <Link href="/manager/audit">
                  <ScrollText />
                  <span>Audit Log</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          <SidebarMenuItem>
            <SidebarMenuButton onClick={logout} tooltip="Log out">
              <LogOut />
              <span>Log out</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />

      <OrganizationModal
        open={openChooseOrg}
        setOpen={setOpenChooseOrg}
        page={orgModalPage}
        setPage={setOrgModalPage}
        startPage="choose"
      />
    </SidebarRoot>
  )
}
